import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import FilterGroup from "../common/reports/FilterGroup";
import FilterItem from "../common/reports/FilterItem";
import Button from "../framework/Button";
import ChipInputSelect from "../framework/ChipInputSelect";
import GlobalContext from "../framework/GlobalContext";
import Shortcut from "../framework/Shortcut";
import TextInput from "../framework/TextInput";
import { displayToast } from "../framework/toast/toastUtils";
import { fetchBranches } from "../services/branch-service";
import { saveStockAuditProducts } from "../services/stock-service";
import classes from "./StockAuditActors.module.css";
import { initial_state_stock_audit } from "./stockAuditConstants";
import StockAuditContext from "./StockAuditContext";
import { listBranchGroup } from "../services/branch-group-services";
import Checkbox from "../framework/Checkbox";
const StockAuditActors = () => {
  const dispatch = useDispatch();
  const { setCurrentComponent } = useContext(GlobalContext);
  const { saveAudit, setSaveAudit, medicineGridRef, saveRef } =
    useContext(StockAuditContext);
  const {
    stockAuditSummary: { totalItem, totalQty, totalLoose },
    productRowData,
    actors: { branches, remarks },
  } = useSelector(
    (state) => state.commonReducer.tabs[state.commonReducer.activeTabIndex]
  );

  const [branchOptions, setBranchOptions] = useState([]);
  const stockAuditBranchesRef = useRef("");
  const validateForm = useCallback(() => {
    if (productRowData.length === 1) {
      displayToast({ type: "error", title: "Please add a product." });
      return false;
    }
    if (!branches || branches?.length === 0) {
      displayToast({ type: "error", title: "Please select a branch." });
      stockAuditBranchesRef.current.focus();
      return false;
    }
    return true;
  }, [productRowData, branches]);

  const closeAc = useCallback(() => {
    setSaveAudit(false);
    medicineGridRef.current.focus();
  }, [medicineGridRef, setSaveAudit]);

  const saveStockAuditData = useCallback(async () => {
    const selectedBranchIds = branches?.reduce((acc, branch) => {
      if (branch?.branchCode) {
        acc.push(...branch.branchCode);
      } else if (branch?.code) {
        acc.push(branch.code);
      }
      return acc;
    }, []);
    const uniqueBranchIds = [...new Set(selectedBranchIds)];

    const stockAuditRequestItems = productRowData.slice(1)?.map((product) => ({
      medicineId: product.medicineId,
      medicine: {
        ...product,
      },
    }));
    const payload = {
      stockAuditRequestItems,
      branchIds: uniqueBranchIds,
      remarks: remarks,
    };
    try {
      const response = await saveStockAuditProducts(payload);
      if (response?.status === "success") {
        dispatch({
          type: "reset_active_tab",
          payload: initial_state_stock_audit,
        });
        displayToast({
          type: "success",
          title: "Stock Audit Request successfully sent.",
        });
        closeAc();
      } else {
        displayToast({
          type: "error",
          title: "Failed to sent stock audit request",
        });
      }
    } catch (error) {
      console.error("Error saving stock audit:", error);
      displayToast({ type: "error", title: "Error saving stock audit" });
    }
  }, [branches, productRowData, remarks, dispatch, closeAc]);

  const saveClickHandler = useCallback(() => {
    if (!validateForm()) return;
    saveStockAuditData();
  }, [validateForm, saveStockAuditData]);
  useEffect(() => {
    if (saveAudit) {
      saveClickHandler();
    }
  }, [saveAudit, saveClickHandler]);
  const fetchAllBranches = useCallback(async () => {
    try {
      const _branch = await fetchBranches();
      const _branchGroup = await listBranchGroup();
      const mergedBranches = [
        ...(_branchGroup?.result?.rows || []),
        ...(_branch?.result?.rows || []),
      ];

      setBranchOptions(mergedBranches || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  }, []);

  useEffect(() => {
    fetchAllBranches();
  }, [fetchAllBranches]);
  return (
    <>
      <div className={classes["stock-audit-container"]}>
        <div className={classes["stock-audit-totals"]}>
          <div className={classes["stock-audit-bill-label"]}>
            Scanned Items (Items/Qty):
          </div>
          <div className={classes["stock-audit-bill-value"]}>
            {totalItem}/{totalQty}
            {totalLoose > 0 ? `::${totalLoose}` : ""}
          </div>
        </div>
        <div className={classes["chip-input-container"]}>
          <ChipInputSelect
            label="Select Branches"
            id="stock_audit_branches"
            key={branches?.length}
            name="branches"
            ref={stockAuditBranchesRef}
            onFocus={() => setCurrentComponent("stock_audit_branches")}
            required={true}
            value={branches || null}
            options={branchOptions}
            openOnFocus={true}
            limitTags={2}
            multiple={true}
            groupBy={(option) => (option?.code ? "Branches" : "Branch Group")}
            getOptionLabel={(option) =>
              `${option?.name || ""}- ${option?.code ? "BR" : "BG"}`
            }
            isOptionEqualToValue={(option, value) => {
              return option === value;
            }}
            onChange={(event, value) => {
              dispatch({
                type: "update_actors",
                payload: { branches: value },
              });
              setTimeout(() => {
                stockAuditBranchesRef.current.focus();
              }, 0);
            }}
            disableCloseOnSelect={true}
            blurOnSelect={false}
            renderGroup={(params) => {
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    padding: "5px",
                    gap: "5px",
                  }}
                  key={params.key}
                >
                  <div
                    style={{
                      height: "30px",
                      width: "100%",
                      background: "#e0e0e0",
                      display: "flex",
                      alignItems: "center",
                      padding: "5px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    {params.group}
                  </div>
                  <div>{params.children}</div>
                </div>
              );
            }}
            renderOption={(props, option, { selected }) => {
              return (
                <div
                  {...props}
                  style={{
                    display: "flex",
                    height: "40px",
                    width: "100%",
                    padding: "1px 5px",
                    gap: "5px",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItem: "center",
                    }}
                  >
                    <Checkbox checked={selected} />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItem: "center",
                      marginRight: "5px",
                    }}
                  >
                    {option?.branchGroupId ? "BG |" : "BR |"}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItem: "center",
                      gap: "5px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        height: "15px",
                        width: "100%",
                      }}
                    >
                      {option?.name} ({option?.code || option?.branchGroupId})
                    </div>
                  </div>
                </div>
              );
            }}
            markers={JSON.stringify({
              up: "",
              down: "stock_audit_remarks",
              left: "",
              right: "stock_audit_remarks",
            })}
          />
        </div>
        <div className={classes["text-input-container"]}>
          <TextInput
            id="stock_audit_remarks"
            label="Remarks"
            value={remarks || ""}
            onChange={(e) => {
              // remarkChangeHandler(e);
              dispatch({
                type: "update_actors",
                payload: { remarks: e.target.value },
              });
            }}
            onFocus={() => setCurrentComponent("stock_audit_remarks")}
            maxInputLength={2000}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "stock_audit_branches",
                down: "stock_audit_save",
                left: "stock_audit_branches",
                right: "stock_audit_save",
              }),
            }}
          />
        </div>
        <div className={classes["stock-audit-ctas"]}>
          <FilterGroup>
            <FilterItem>
              <Button
                id="stock_audit_save"
                ref={saveRef}
                label="Save"
                color="violet"
                onClick={saveClickHandler}
                onFocus={() => {
                  setCurrentComponent("stock_audit_save");
                }}
                endIcon={<Shortcut keys={["Ctrl", "S"]} />}
              />
            </FilterItem>
          </FilterGroup>
        </div>
      </div>
    </>
  );
};

export default StockAuditActors;
